import { bem, Chatter, EMOJI_SHORTCODE, searchShortcodes, selectBroadcastGuestsArray } from '@socialive/common';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mention, MentionsInput } from 'react-mentions';
import { useSelector } from 'react-redux';
import { map } from 'rxjs/operators';
import { useObservableState } from '../../../hooks';
import ArrowRightIcon from '../../Icons/ArrowRightIcon';
import './chatInput.css';
import processShrug from './processShrug';
import ShrugSuggestion from './ShrugSuggestion';

const neverMatchingRegex = /($a)/;

/*
#tailwind
chatInput__input__suggestions chatInput__input__suggestions__item chatInput__input__suggestions__item--focused
*/

function ChatInput({ testId, placeholder, onSend, className }) {
  const [message, setMessage] = useState('');
  const [options, setOptions] = useState([]);
  const [search$, onSearch] = useObservableState();
  const ref = useRef();

  useEffect(() => {
    const sub = search$.pipe(map(searchShortcodes)).subscribe(setOptions);
    return () => sub.unsubscribe();
  }, [search$]);

  const guests = useSelector(selectBroadcastGuestsArray);

  useEffect(() => {
    const parsed = Chatter.parse(message, { users: guests });
    const compiled = parsed.toSource();
    if (compiled !== message) {
      setMessage(compiled);
    }
  }, [message, guests]);

  const atData = useCallback(
    (search) => {
      const lower = search.toLowerCase();
      return guests
        .filter((guest) => guest.name.toLowerCase().includes(lower))
        .map((guest) => ({ id: guest.userId, display: `@${guest.name}` }))
        .filter(Boolean)
        .slice(0, 5);
    },
    [guests]
  );

  const slashData = useMemo(
    () => [{ id: 'shrug', display: '/shrug', Suggestion: ShrugSuggestion }].filter(Boolean),
    []
  );

  const sendMessage = useCallback(
    (event) => {
      event?.preventDefault();
      const trimmed = processShrug(message).trim();
      if (trimmed) {
        onSend(trimmed);
      }
      setMessage('');
    },
    [message, onSend]
  );

  const handleSetMessage = (e) => {
    setMessage(e.target.value);
    const searchTerm = e.target.value.substring(e.target.value.indexOf(':') + 1);
    onSearch(searchTerm);
  };

  const emojiData = useMemo(
    () =>
      options?.map((option) => ({
        id: EMOJI_SHORTCODE[option],
        display: `${EMOJI_SHORTCODE[option]} ${option}`,
      })) ?? [],
    [options]
  );

  const onSlashRenderSuggestion = useCallback(({ Suggestion, display }) => <Suggestion /> ?? display, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.keyCode === 13 && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  const fieldTestId = testId && `${testId}-field`;
  const buttonTestId = testId && `${testId}-button`;

  return (
    <div className={bem`chatInput ${className}`} data-testid={testId}>
      <MentionsInput
        inputRef={ref}
        value={message}
        data-testid={fieldTestId}
        onKeyDown={handleKeyDown}
        onChange={handleSetMessage}
        placeholder={placeholder}
        a11ySuggestionsListLabel={placeholder}
        className="chatInput__input"
        allowSpaceInQuery
      >
        <Mention trigger=":" data={emojiData} markup="__id__" appendSpaceOnAdd regex={neverMatchingRegex} />
        <Mention trigger="@" data={atData} markup="__display__" appendSpaceOnAdd regex={neverMatchingRegex} />
        <Mention
          trigger="/"
          data={slashData}
          markup="__display__"
          renderSuggestion={onSlashRenderSuggestion}
          regex={neverMatchingRegex}
        />
      </MentionsInput>
      <button
        type="button"
        data-testid={buttonTestId}
        className="chatInput__button"
        onClick={sendMessage}
        disabled={!message.trim()}
      >
        <ArrowRightIcon />
      </button>
    </div>
  );
}

ChatInput.propTypes = {
  testId: PropTypes.string,
  placeholder: PropTypes.string,
  onSend: PropTypes.func,
  className: PropTypes.string,
};

ChatInput.defaultProps = {
  testId: undefined,
  placeholder: undefined,
  onSend: undefined,
  className: undefined,
};

export default ChatInput;
